import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    EditorFilter,
    EditorFilterMessage,
    EditorFilterMessageTypes,
} from "./types";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { getBadgeClassName } from "~/components/Badge/component";
import { faBroomWide } from "@fortawesome/pro-solid-svg-icons";
import { areFiltersIdentical } from "../areFiltersIdentical";

// // // //

export function getFirstMessage(props: {
    filter: EditorFilter;
}): EditorFilterMessage {
    const { messages } = props.filter;

    const errorMessage = messages.find((m) => m.variant === "error");
    if (errorMessage) {
        return errorMessage;
    }

    const warningMessage = messages.find((m) => m.variant === "warning");
    if (warningMessage) {
        return warningMessage;
    }

    return messages[0];
}

/**
 * FilterMessagesDisplay
 * Displays warning + error messages for EditorFilter
 */
export function FilterMessagesDisplay(props: {
    filter: EditorFilter;
    onRemove: () => void;
    onChange: (updatedEditorFilter: EditorFilter) => void;
}) {
    const { filter, onRemove, onChange } = props;
    const { messages } = filter;

    // Return null if there are no messages
    if (messages.length === 0) {
        return null;
    }

    // Get the first message - we only display one message at a time for now.
    const message = getFirstMessage({ filter });

    // Construct the message we display to users
    let userMessage = <></>;

    // // // //
    // TODO - Basic Filter Validations

    // Handle missingField message type
    if (message.type === EditorFilterMessageTypes.missingField) {
        userMessage = <span>Missing field in filter.</span>;
    }

    // Handle missingOperator message type
    if (message.type === EditorFilterMessageTypes.missingOperator) {
        userMessage = <span>Missing operator in filter.</span>;
    }

    // Handle missingValue message type
    if (message.type === EditorFilterMessageTypes.missingValue) {
        userMessage = <span>Missing value in filter.</span>;
    }

    // Handle duplicateBasicFilterInsideAndClause message type
    if (
        message.type ===
        EditorFilterMessageTypes.duplicateBasicFilterInsideAndClause
    ) {
        userMessage = <span>Duplicate filter.</span>;
    }
    // Handle identicaBasicFilterInsideGroup message type
    if (
        message.type === EditorFilterMessageTypes.identicaBasicFilterInsideGroup
    ) {
        userMessage = <span>Identical sibling filter.</span>;
    }

    //
    // // // //

    // Handle andOperatorContainsDuplicateFields message type
    if (
        message.type ===
        EditorFilterMessageTypes.andOperatorContainsDuplicateFields
    ) {
        // Get the duplicate count
        // Note - this only handles *one* collection of duplicated fields at a time
        const duplicateCount =
            filter.nested[0].messages.filter((m) => {
                return (
                    m.type ===
                    EditorFilterMessageTypes.duplicateBasicFilterInsideAndClause
                );
            }).length + 1;
        userMessage = (
            <span className="flex items-center gap-2">
                There are {duplicateCount} filters for the same property in this
                group.{" "}
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        // Remove nested duplicate items from filter.nested here
                        const updatedNested = filter.nested.filter(
                            (nestedFilter, index, nestedArray) =>
                                nestedArray.findIndex(
                                    (siblingFilter) =>
                                        siblingFilter.state.field ===
                                        nestedFilter.state.field,
                                ) === index,
                        );
                        const updatedFilter: EditorFilter = {
                            ...filter,
                            nested: updatedNested,
                        };
                        onChange(updatedFilter);
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Handle redundantParentComposite message type
    if (message.type === EditorFilterMessageTypes.redundantParentComposite) {
        userMessage = (
            <span className="flex items-center gap-2 whitespace-nowrap">
                This PARENT is redundant with only a single filter inside.
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        if (filter.nested[0]) {
                            onChange({
                                ...filter.nested[0],
                            });
                        }
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Handle redundantComposite message type
    // TODO - finish implementing this
    if (message.type === EditorFilterMessageTypes.redundantComposite) {
        userMessage = (
            <span className="flex items-center gap-2">
                This nested filter group is redundant
                {/* <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        // TODO - there needs to be a way to handle this edge case -> THIS MUST CHANGE THE FILTERS ON THE PARENT
                        // Perhaps we add props.onChangeParent()??
                        // if (filter.nested[0]) {
                        //     onChange({
                        //         ...filter.nested[0],
                        //     });
                        // }
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button> */}
            </span>
        );
    }

    // Handle redundantAndClause message type
    if (message.type === EditorFilterMessageTypes.redundantAndClause) {
        userMessage = (
            <span className="flex items-center gap-2">
                This group is redundant with only a single filter inside.
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        if (filter.nested[0]) {
                            onChange({
                                ...filter.nested[0],
                            });
                        }
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Handle redundantOrClause message type
    if (message.type === EditorFilterMessageTypes.redundantOrClause) {
        userMessage = (
            <span className="flex items-center gap-2">
                This group is redundant with only a single filter inside.
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        if (filter.nested[0]) {
                            onChange({
                                ...filter.nested[0],
                            });
                        }
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Handle redundantCongruentParentComposite message type
    // TODO
    if (
        message.type ===
        EditorFilterMessageTypes.redundantCongruentParentComposite
    ) {
        userMessage = (
            <span className="flex items-center gap-2">
                This group is redundant with only a single group inside.
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        if (filter.nested[0]) {
                            onChange({
                                ...filter.nested[0],
                            });
                        }
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Handle emptyComposite message type
    // TODO
    if (message.type === EditorFilterMessageTypes.emptyComposite) {
        userMessage = (
            <span className="flex items-center gap-2">
                This group is redundant without any filters.{" "}
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        onRemove();
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Handle groupContainsIdenticalFields message type
    if (
        message.type === EditorFilterMessageTypes.groupContainsIdenticalFields
    ) {
        const identicalFilterCount = filter.messages.filter(
            (e) =>
                e.type ===
                EditorFilterMessageTypes.groupContainsIdenticalFields,
        ).length;

        userMessage = (
            <span className="flex items-center gap-2">
                This group contains{" "}
                {identicalFilterCount > 1
                    ? `${identicalFilterCount} filters that are`
                    : "a filter that is"}{" "}
                identical to another.{" "}
                <button
                    className="underline flex items-center gap-0.5"
                    onClick={() => {
                        // Remove nested duplicate items from filter.nested here
                        const updatedNested = filter.nested.filter(
                            (nestedFilter, index, nestedArray) =>
                                nestedArray.findIndex((siblingFilter) =>
                                    areFiltersIdentical({
                                        currentFilter: siblingFilter.state,
                                        latestFilter: nestedFilter.state,
                                    }),
                                ) === index,
                        );
                        const updatedFilter: EditorFilter = {
                            ...filter,
                            nested: updatedNested,
                        };
                        onChange(updatedFilter);
                    }}
                >
                    <FontAwesomeIcon className="h-3 w-3" icon={faBroomWide} />
                    Cleanup
                </button>
            </span>
        );
    }

    // Render userMessage TSX inside badge element
    return (
        <div
            className={getBadgeClassName({
                rounded: "full",
                variant: message.variant === "warning" ? "yellow" : "red",
                className:
                    "justify-center text-center flex items-center gap-1 text-xs",
            })}
        >
            <FontAwesomeIcon className="h-3 w-3 mr-1" icon={faInfoCircle} />
            <span className="min-w-24">{userMessage}</span>
        </div>
    );
}
